import { useState, useEffect } from "react";
import { db } from "../db/localDB";
import {
  createVisitaBac as apiCrearVisita,
  updateVisitaBac as apiActualizarVisita,
  deleteVisitaBac as apiEliminarVisita,
  getVisitasBac   as apiGetVisitas,
  subirFotoVivienda as apiSubirFoto,
  adjuntarFotoAVisita as apiAdjuntarFoto,
  adjuntarFotoCarnet as apiAdjuntarFotoCarnet,
} from "../bac_sarampion/api/bacSarampionAPI";
import { base64ABlob } from "../bac_sarampion/utils/imageCompression";
import { sincronizarJornadasPendientes, contarJornadasPendientes } from "../bac_sarampion/jornadas/jornadasBac";
import {
  sincronizarDisentimientosBac,
  contarDisentimientosPendientesBac,
} from "../bac_sarampion/disentimiento/disentimientoSync";

const esIdLocal    = (id) => typeof id === "string" && id.startsWith("local_");
const extraerLocalId = (id) => parseInt(String(id).replace(/^local_/, ""), 10);

// Arma un mensaje legible a partir del error del backend, para guardarlo
// en "ultimoError" y mostrarlo en el panel de sincronización.
function motivoLegible(error) {
  return error?.response?.data?.message || error?.message || "Error desconocido";
}

// ════════════════════════════════════════════════════════════════
// ESTADO DE CONEXIÓN
// ════════════════════════════════════════════════════════════════
export function useOnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on  = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online",  on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online",  on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

// ════════════════════════════════════════════════════════════════
// GUARDAR OFFLINE — Visitas (crear / editar / eliminar)
// ════════════════════════════════════════════════════════════════

// payload puede traer "codigo_vivienda_previo" cuando es una revisita
// (lo agrega BacSarampionModal.jsx) — se guarda tal cual, el backend
// resuelve el numero_visita al sincronizar.
export async function guardarOfflineBacVisitaCrear(payload) {
  try {
    const { id, updatedAt, createdAt: _c, ...limpio } = payload;

    // Guardia contra doble-envío (doble toque en pantalla lenta): si ya
    // hay un pendiente de creación sin sincronizar para el mismo intento
    // (mismo codigo_vivienda / codigo_vivienda_previo), se actualiza esa
    // fila en vez de duplicarla.
    const clave = limpio.codigo_vivienda_previo
      ? { campo: "codigo_vivienda_previo", valor: limpio.codigo_vivienda_previo }
      : { campo: "codigo_vivienda", valor: limpio.codigo_vivienda };

    if (clave.valor) {
      const existente = await db.bac_visitas_pendientes
        .filter(r => r.sincronizado === 0 && !r._editId && !r._deleteId && r[clave.campo] === clave.valor)
        .first();
      if (existente) {
        await db.bac_visitas_pendientes.update(existente.id, { ...limpio });
        return existente.id;
      }
    }

    const localId = await db.bac_visitas_pendientes.add({
      ...limpio,
      sincronizado: 0,
      createdAt: new Date().toISOString(),
    });
    console.log("💾 Guardada offline visita BAC Sarampión, localId:", localId);
    return localId;
  } catch (e) {
    console.error("❌ Error guardando offline visita BAC Sarampión:", e);
    throw e;
  }
}

// Edita un borrador que todavía NO ha sincronizado — actualiza la fila
// local directamente en el dispositivo, sin llamar al servidor para
// nada. Cuando la sincronización normal corra (sola, al recuperar
// conexión), sale con los datos ya corregidos, como si siempre hubieran
// sido así.
export async function actualizarBorradorPendiente(localId, payload) {
  // Chequeo de seguridad: si en el rato que la persona estuvo editando
  // este borrador, la sincronización normal ya lo mandó al servidor
  // (pudo recuperar señal justo en ese momento), escribir acá encima ya
  // no sirve de nada — esa copia local no la vuelve a mirar nadie, y la
  // edición se perdería en silencio. Se avisa en vez de guardar a ciegas.
  const actual = await db.bac_visitas_pendientes.get(localId);
  if (!actual) {
    return { ok: false, motivo: "ya_no_existe" };
  }
  if (actual.sincronizado !== 0) {
    return { ok: false, motivo: "ya_sincronizado" };
  }

  const { id, updatedAt, createdAt, sincronizado, _localId, _pendiente, ...limpio } = payload;
  await db.bac_visitas_pendientes.update(localId, { ...limpio });
  return { ok: true };
}


// Editar UN intento puntual (no crea revisita — para eso es guardarOfflineBacVisitaCrear
// con codigo_vivienda_previo).
export async function guardarOfflineBacVisitaEditar(visitaId, payload) {
  try {
    const { id, updatedAt, createdAt: _c, ...limpio } = payload;

    if (esIdLocal(visitaId)) {
      const localId = extraerLocalId(visitaId);
      await db.bac_visitas_pendientes.update(localId, { ...limpio });
      return localId;
    }

    const localId = await db.bac_visitas_pendientes.add({
      ...limpio,
      _editId: visitaId,
      sincronizado: 0,
      createdAt: new Date().toISOString(),
    });
    return localId;
  } catch (e) {
    console.error("❌ Error guardando edición offline visita BAC Sarampión:", e);
    throw e;
  }
}

export async function guardarOfflineBacVisitaEliminar(visitaId) {
  try {
    if (esIdLocal(visitaId)) {
      await db.bac_visitas_pendientes.delete(extraerLocalId(visitaId));
      return;
    }
    await db.bac_visitas_pendientes.add({
      _deleteId: visitaId,
      sincronizado: 0,
      createdAt: new Date().toISOString(),
    });
  } catch (e) {
    console.error("❌ Error guardando eliminación offline visita BAC Sarampión:", e);
    throw e;
  }
}

// ════════════════════════════════════════════════════════════════
// CACHE DE LECTURA (para ver tabla/mapa offline)
// ════════════════════════════════════════════════════════════════
export async function cachearBacVisitas(territorio, comunidad, visitas) {
  try {
    await db.bac_visitas_cache.put({
      territorio: territorio || "",
      comunidad:  comunidad  || "",
      visitas:    JSON.stringify(visitas),
      cachedAt:   new Date().toISOString(),
    });
  } catch (e) { console.error("Error cacheando visitas BAC Sarampión:", e); }
}

export async function leerBacVisitasCache(territorio, comunidad) {
  try {
    const rec = await db.bac_visitas_cache.get([territorio || "", comunidad || ""]);
    return rec ? JSON.parse(rec.visitas) : [];
  } catch { return []; }
}

// ── Caché de la pestaña PAI — mismo patrón que las visitas ─────────────
export async function cachearPaiVacunacion(territorio, comunidad, registros) {
  try {
    await db.bac_pai_cache.put({
      territorio: territorio || "",
      comunidad:  comunidad  || "",
      registros:  JSON.stringify(registros),
      cachedAt:   new Date().toISOString(),
    });
  } catch (e) { console.error("Error cacheando PAI:", e); }
}

export async function leerPaiVacunacionCache(territorio, comunidad) {
  try {
    const rec = await db.bac_pai_cache.get([territorio || "", comunidad || ""]);
    return rec ? JSON.parse(rec.registros) : [];
  } catch { return []; }
}

// ── Caché de la pestaña MRCV — mismo patrón ─────────────────────────────
export async function cachearMrcv(territorio, comunidad, registros) {
  try {
    await db.bac_mrcv_cache.put({
      territorio: territorio || "",
      comunidad:  comunidad  || "",
      registros:  JSON.stringify(registros),
      cachedAt:   new Date().toISOString(),
    });
  } catch (e) { console.error("Error cacheando MRCV:", e); }
}

export async function leerMrcvCache(territorio, comunidad) {
  try {
    const rec = await db.bac_mrcv_cache.get([territorio || "", comunidad || ""]);
    return rec ? JSON.parse(rec.registros) : [];
  } catch { return []; }
}

// ════════════════════════════════════════════════════════════════
// ESTADO DE SINCRONIZACIÓN Y MANEJO DE ERRORES
// ════════════════════════════════════════════════════════════════
// Estados posibles en "sincronizado":
//   0 = pendiente por enviar (se reintenta en cada sync)
//   1 = ya sincronizado
//   3 = ERROR DE DATOS al sincronizar — el motivo queda en "ultimoError"

// bac_disentimiento_pendientes: constancias de disentimiento hechas sin
// señal — entran al mismo panel de pendientes/errores que las visitas.
// bac_jornadas_pendientes: jornadas BAC abiertas en campo sin señal.
const TABLAS_BAC_PENDIENTES = ["bac_jornadas_pendientes", "bac_visitas_pendientes", "bac_disentimiento_pendientes"];

// Cómo se identifica cada registro en el panel de pendientes
const referenciaPendiente = (tabla, r) =>
  tabla === "bac_jornadas_pendientes"
    ? `Jornada BAC — ${r.comunidad || ""} (${r.codigo || `registro #${r.id}`})`
    : tabla === "bac_disentimiento_pendientes"
    ? `Disentimiento PAI — ${r.menor_nombre || r.codigo || `registro #${r.id}`}`
    : (r.codigo_vivienda || r.codigo_vivienda_previo || `registro #${r.id}`);

export async function obtenerEstadoSincronizacionBac() {
  const resumen = { pendientes: 0, errores: 0, detalleErrores: [], detallePendientes: [] };
  for (const tabla of TABLAS_BAC_PENDIENTES) {
    const pend = await db[tabla].filter(r => r.sincronizado === 0).toArray();
    const err  = await db[tabla].filter(r => r.sincronizado === 3).toArray();
    resumen.pendientes += pend.length;
    resumen.errores += err.length;
    resumen.detallePendientes.push(...pend.map(r => ({
      tabla, id: r.id, tipo: "pendiente",
      motivo: "Esperando conexión o el próximo intento de sincronización.",
      referencia: referenciaPendiente(tabla, r),
    })));
    resumen.detalleErrores.push(...err.map(r => ({
      tabla, id: r.id, tipo: "error",
      motivo: r.ultimoError || "Error desconocido",
      referencia: referenciaPendiente(tabla, r),
    })));
  }
  return resumen;
}

export async function reintentarPendienteBac(tabla, id) {
  if (!TABLAS_BAC_PENDIENTES.includes(tabla)) return;
  await db[tabla].update(id, { sincronizado: 0, ultimoError: null });
}

export async function descartarUnPendienteBac(tabla, id) {
  if (!TABLAS_BAC_PENDIENTES.includes(tabla)) return;
  await db[tabla].delete(id);
}

export async function descartarPendientesConErrorBac() {
  let total = 0;
  for (const tabla of TABLAS_BAC_PENDIENTES) {
    const err = await db[tabla].filter(r => r.sincronizado === 3).toArray();
    await db[tabla].bulkDelete(err.map(r => r.id));
    total += err.length;
  }
  return total;
}

export async function limpiarCacheBac() {
  await db.bac_visitas_cache.clear();
}

export async function limpiarTodosPendientesBac() {
  let total = 0;
  for (const tabla of TABLAS_BAC_PENDIENTES) {
    total += await db[tabla].count();
    await db[tabla].clear();
  }
  return total;
}

export async function contarPendientesBac() {
  const visitas = await db.bac_visitas_pendientes.where("sincronizado").equals(0).count();
  const disentimientos = await contarDisentimientosPendientesBac().catch(() => 0);
  const jornadas = await contarJornadasPendientes().catch(() => 0);
  return visitas + disentimientos + jornadas;
}

// ════════════════════════════════════════════════════════════════
// SINCRONIZAR TODOS LOS PENDIENTES
// ════════════════════════════════════════════════════════════════
// Respeta el orden de creación (Dexie .toArray() ya devuelve en orden
// de inserción), así una revisita siempre sincroniza DESPUÉS del
// intento anterior sobre la misma vivienda, y el backend calcula bien
// el numero_visita.
// Si ya hay una sincronización corriendo (p. ej. el evento "online" y el
// botón "Sincronizar" al mismo tiempo), se reutiliza la misma en vez de
// lanzar otra en paralelo que podría enviar dos veces el mismo registro.
let syncBacEnCurso = null;
export function sincronizarPendientesBac() {
  if (!syncBacEnCurso) {
    syncBacEnCurso = sincronizarPendientesBacInterno().finally(() => { syncBacEnCurso = null; });
  }
  return syncBacEnCurso;
}

async function sincronizarPendientesBacInterno() {
  // Primero las jornadas abiertas sin señal — las visitas van dentro de
  // ellas (y si hubo choque con otra jornada, aquí se reasignan).
  await sincronizarJornadasPendientes().catch((e) => console.error("❌ Sync jornadas BAC:", e?.message));

  const pendientes = await db.bac_visitas_pendientes
    .filter(r => r.sincronizado === 0 || r.sincronizado === false)
    .toArray();

  let sincronizados = 0;
  const territoriosConCambios = new Set();

  for (const r of pendientes) {
    try {
      const { id, sincronizado, createdAt, _editId, _deleteId, ...payload } = r;

      if (_deleteId) {
        await apiEliminarVisita(_deleteId);
      } else if (_editId) {
        await apiActualizarVisita(_editId, payload);
      } else {
        await apiCrearVisita(payload); // incluye codigo_vivienda_previo si es revisita
      }

      await db.bac_visitas_pendientes.update(r.id, { sincronizado: 1 });
      territoriosConCambios.add(JSON.stringify({ territorio: r.territorio, comunidad: r.comunidad }));
      sincronizados++;
    } catch (e) {
      const status = e?.response?.status;
      if ([400, 404, 409, 413, 422].includes(status)) {
        const motivo = motivoLegible(e);
        console.error("❌ Error de datos sync visita BAC Sarampión:", r.id, motivo);
        await db.bac_visitas_pendientes.update(r.id, { sincronizado: 3, ultimoError: motivo });
        window.dispatchEvent(new CustomEvent("bac-error-sincronizacion", {
          detail: { tabla: "bac_visitas_pendientes", id: r.id, motivo, codigo_vivienda: r.codigo_vivienda },
        }));
      } else {
        // Error de conectividad/servidor — se queda pendiente (0) para
        // reintentar en el siguiente ciclo, sin marcar error.
        console.error("❌ Error sync visita BAC Sarampión:", e.message);
      }
    }
  }

  for (const claveJson of territoriosConCambios) {
    try {
      const { territorio, comunidad } = JSON.parse(claveJson);
      const res = await apiGetVisitas({ territorio, comunidad });
      await cachearBacVisitas(territorio, comunidad, res.data || []);
    } catch (e) {
      console.warn("⚠️ No se pudo actualizar cache visitas BAC Sarampión:", e.message);
    }
  }

  console.log(`✅ Sync BAC Sarampión completo: ${sincronizados} registros`);

  // Encadenado a propósito: apenas terminan de sincronizar las visitas es
  // el momento correcto para reintentar las fotos que se quedaron
  // esperando (su vivienda puede que recién ahora exista en el servidor).
  await sincronizarFotosBac().catch(() => {});

  // Constancias de disentimiento hechas sin señal
  const disentimientos = await sincronizarDisentimientosBac().catch(() => 0);

  window.dispatchEvent(new CustomEvent("sync-completado"));
  return { bacVisitas: sincronizados, disentimientos };
}

// ════════════════════════════════════════════════════════════════
// FOTOS — cola SEPARADA, viaja independiente de la visita
// ════════════════════════════════════════════════════════════════

// Guarda la foto YA COMPRIMIDA (base64) en la cola local, ligada al
// codigo_vivienda (nunca a un id de fila — la visita puede seguir
// offline en ese momento). "campo" distingue a dónde se adjunta al
// sincronizar: "vivienda" (foto de la casa), "carnet_a" o "carnet_b"
// (carnet de vacunación — en ese caso "refId" es el número de
// identificación de la persona, para saber CUÁL carnet es, ya que
// puede haber varios por vivienda).
export async function guardarFotoOffline(codigoVivienda, base64Comprimido, nombreArchivo, campo = "vivienda", refId = null) {
  try {
    const localId = await db.bac_fotos_pendientes.add({
      codigo_vivienda: codigoVivienda,
      campo,
      refId,
      base64: base64Comprimido,
      nombre: nombreArchivo,
      sincronizado: 0,
      createdAt: new Date().toISOString(),
    });
    console.log("💾 Foto guardada localmente, localId:", localId);
    return localId;
  } catch (e) {
    console.error("❌ Error guardando foto localmente:", e);
    throw e;
  }
}

// Trae TODAS las fotos que están guardadas offline (sin sincronizar
// todavía) para una vivienda — la de la casa y las de cada carnet. Se
// usa al abrir "Editar" sobre un borrador pendiente, para que las fotos
// ya tomadas sí se vean, en vez de aparecer como si no existieran.
export async function leerFotosPendientesDeVivienda(codigoVivienda) {
  if (!codigoVivienda) return [];
  return db.bac_fotos_pendientes
    .filter(f => f.sincronizado === 0 && f.codigo_vivienda === codigoVivienda)
    .toArray();
}

// Libera espacio del dispositivo borrando SOLO lo que es caché —
// copias locales de datos que ya están a salvo en el servidor y que se
// vuelven a descargar solas la próxima vez que haga falta. NUNCA toca
// la cola de pendientes (bac_visitas_pendientes, bac_fotos_pendientes)
// — eso es trabajo de campo sin sincronizar todavía, borrarlo sí sería
// perderlo de verdad. También limpia la caché del service worker (los
// archivos de la app misma), que se vuelve a descargar sola al recargar.
// Limpieza retroactiva — antes del cambio a "borrar en vez de marcar",
// las fotos ya sincronizadas se quedaban guardadas localmente para
// siempre (marcadas sincronizado:1, pero sin borrarse nunca). En
// celulares que llevan tiempo usando la app, eso se fue acumulando y
// es justo lo que más pesa. Se borra sola, una sola vez por
// dispositivo — sin que nadie tenga que ir celular por celular.
const CLAVE_LIMPIEZA_HECHA = "bac_limpieza_fotos_viejas_v1";
export async function limpiarFotosYaSincronizadasBac() {
  if (localStorage.getItem(CLAVE_LIMPIEZA_HECHA)) return { limpiadas: 0, yaHecha: true };

  const viejas = await db.bac_fotos_pendientes.where("sincronizado").notEqual(0).toArray();
  if (viejas.length > 0) {
    await db.bac_fotos_pendientes.bulkDelete(viejas.map(f => f.id));
    console.log(`🧹 Limpieza retroactiva: ${viejas.length} foto(s) ya sincronizada(s) borrada(s) del dispositivo`);
  }
  localStorage.setItem(CLAVE_LIMPIEZA_HECHA, new Date().toISOString());
  return { limpiadas: viejas.length, yaHecha: false };
}

export async function liberarEspacioCacheBac() {
  await db.bac_visitas_cache.clear();
  await db.bac_catalogos_cache.clear();
  await db.bac_pai_cache.clear();
  await db.bac_mrcv_cache.clear();
  await db.bac_disentimiento_cache.clear();

  if ("caches" in window) {
    const nombres = await caches.keys();
    await Promise.all(nombres.map(n => caches.delete(n)));
  }
}

// Fotos que llevan varios intentos fallidos sin poder subir — para
// mostrar el detalle directo en pantalla (motivo real del error) y
// dejar que la persona decida si las descarta, sin depender de
// herramientas de desarrollador.
export async function leerFotosAtascadasBac() {
  return db.bac_fotos_pendientes
    .filter(r => r.sincronizado === 0 && (r.intentosFallidos || 0) >= 5)
    .toArray();
}

export async function descartarFotoPendienteBac(id) {
  await db.bac_fotos_pendientes.delete(id);
}

export async function contarFotosPendientesBac() {
  return db.bac_fotos_pendientes.where("sincronizado").equals(0).count();
}

// Sube y adjunta cada foto pendiente. Si la visita dueña del
// codigo_vivienda todavía no sincronizó (el backend responde 404), la
// foto se queda pendiente (NO se marca error) para reintentar en el
// siguiente ciclo — normalmente después de que sincronicen las visitas.
export async function sincronizarFotosBac() {
  const pendientes = await db.bac_fotos_pendientes
    .filter(r => r.sincronizado === 0)
    .toArray();

  let sincronizadas = 0;

  for (const r of pendientes) {
    try {
      const blob = base64ABlob(r.base64);
      const fd = new FormData();
      fd.append("archivo", blob, r.nombre || "foto.jpg");

      const resSubida = await apiSubirFoto(fd);
      const { url, nombre } = resSubida.data;

      if (r.campo === "carnet_a" || r.campo === "carnet_b" || r.campo === "carnet_paiweb") {
        const campoServidor = r.campo === "carnet_a" ? "a" : r.campo === "carnet_b" ? "b" : "paiweb";
        await apiAdjuntarFotoCarnet(r.codigo_vivienda, r.refId, campoServidor, { foto_url: url, foto_nombre: nombre });
      } else {
        await apiAdjuntarFoto(r.codigo_vivienda, { foto_url: url, foto_nombre: nombre });
      }

      // Se BORRA (no solo se marca) — ya viajó y quedó en el servidor,
      // no hay razón para seguir ocupando espacio en el dispositivo con
      // el base64 de una foto que ya no hace falta tener localmente.
      await db.bac_fotos_pendientes.delete(r.id);
      sincronizadas++;
    } catch (e) {
      const status = e?.response?.status;

      if (status === 404) {
        // ¿Por qué no existe la visita en el servidor? Puede ser que
        // todavía no le toque el turno (normal, se reintenta luego), o
        // que esa visita ya falló de forma PERMANENTE (datos inválidos,
        // sincronizado:3) — en ese caso esta foto JAMÁS va a poder
        // sincronizar, porque la vivienda dueña nunca va a existir en
        // el servidor. Sin este chequeo, se quedaría reintentando para
        // siempre y acumulando espacio en el dispositivo sin necesidad.
        const visitaDueña = await db.bac_visitas_pendientes
          .where("codigo_vivienda").equals(r.codigo_vivienda)
          .and(v => v.sincronizado === 3)
          .first();

        if (visitaDueña) {
          console.error(`❌ Foto de ${r.codigo_vivienda} descartada — la visita dueña falló permanentemente: ${visitaDueña.ultimoError || "motivo desconocido"}`);
          await db.bac_fotos_pendientes.delete(r.id);
          window.dispatchEvent(new CustomEvent("bac-error-sincronizacion", {
            detail: { tabla: "bac_fotos_pendientes", id: r.id, motivo: "La visita de esta foto no se pudo guardar, así que la foto tampoco pudo subirse.", codigo_vivienda: r.codigo_vivienda },
          }));
          continue;
        }
        // La visita todavía no sincronizó — normal, se reintenta después,
        // no es un error real de esta foto.
        console.log(`⏭ Foto de ${r.codigo_vivienda} esperando a que la visita sincronice`);
        continue;
      }

      if (status === 400 || status === 413 || status === 422) {
        // El servidor rechazó el DATO en sí (archivo dañado, demasiado
        // pesado, formato inválido) — reintentar no cambia nada, el
        // problema no es la conexión, es la foto misma. Se descarta y
        // se avisa claro, para que sepan que hay que volver a tomarla.
        console.error(`❌ Foto de ${r.codigo_vivienda} descartada — el servidor la rechazó (${status}), no es un problema de conexión.`);
        await db.bac_fotos_pendientes.delete(r.id);
        window.dispatchEvent(new CustomEvent("bac-error-sincronizacion", {
          detail: { tabla: "bac_fotos_pendientes", id: r.id, motivo: "El servidor rechazó esta foto (archivo dañado o demasiado pesado) — hay que volver a tomarla.", codigo_vivienda: r.codigo_vivienda },
        }));
        continue;
      }

      // Cualquier otro caso (sin señal, tiempo agotado, error del
      // servidor puntual) SÍ debe seguir reintentando — es justo lo que
      // hace posible que la app funcione con mala conexión. Pero si
      // lleva varios intentos fallidos seguidos sin ser nunca alguno de
      // los casos de arriba, algo raro pasa — se guarda el motivo real
      // (para poder verlo directo en la pantalla, sin herramientas de
      // desarrollador) y se avisa, sin borrar — la persona decide.
      const intentosPrevios = (r.intentosFallidos || 0) + 1;
      const motivoReal = status ? `Error del servidor (código ${status}): ${e.message}` : `Sin respuesta del servidor: ${e.message}`;
      await db.bac_fotos_pendientes.update(r.id, { intentosFallidos: intentosPrevios, ultimoError: motivoReal });
      if (intentosPrevios === 5) {
        console.warn(`⚠️ Foto de ${r.codigo_vivienda} lleva ${intentosPrevios} intentos fallidos — ${motivoReal}`);
        window.dispatchEvent(new CustomEvent("bac-error-sincronizacion", {
          detail: { tabla: "bac_fotos_pendientes", id: r.id, motivo: `Esta foto lleva varios intentos sin poder subir. Motivo: ${motivoReal}`, codigo_vivienda: r.codigo_vivienda },
        }));
      }
      console.error("❌ Error sync foto BAC Sarampión:", motivoReal);
    }
  }

  if (sincronizadas > 0) window.dispatchEvent(new CustomEvent("sync-completado"));
  console.log(`✅ Sync fotos BAC Sarampión: ${sincronizadas} de ${pendientes.length}`);
  return sincronizadas;
}

// Reintenta el sync automáticamente al recuperar conexión.
window.addEventListener("online", () => {
  sincronizarPendientesBac().catch(() => {});
  sincronizarFotosBac().catch(() => {});
});